// <pos-scheduling/> — AI 班表：選週 → 一鍵生成 → 預覽 → 發布
// 路徑：#/scheduling（owner only，MVP owner 代勞模式）
// 生成走 /scheduling-generate（Layer 0 需求預測 + solver）；讀/發布直連 supabase（RLS）
// see architecture.md#L980 + docs/SCHEDULING_SYSTEM_SPEC.md v0.2

import { getSupabase } from '../lib/supabase-client.js';
import { generateSchedule } from '../lib/api.js';
import { escapeHtml } from '../lib/utils/escape.js';

const WD_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const ROLE_LABELS = { chef: '主廚', fulltime: '正職', parttime: '兼職' };

function shiftHrs(start, end) {
  const [sh, sm] = String(start).split(':').map(Number);
  const [eh, em] = String(end).split(':').map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
}

function addDays(dateStr, n) {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
/** 下週一（店長心智模型：排「下週」的班） */
function nextMonday() {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const wd = new Date(`${today}T00:00:00Z`).getUTCDay();
  return addDays(today, ((8 - wd) % 7) || 7);
}
function fmtMD(dateStr) {
  const [, m, d] = dateStr.split('-');
  return `${Number(m)}/${Number(d)}`;
}

class PosScheduling extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      weekStart: nextMonday(),
      schedule: null,       // 當週 schedule row（published 優先，否則最新 draft）
      assignments: [],
      templates: [],
      employees: [],
      violations: [],       // 只在剛生成完的 session 內有
      statsScope: 'week',   // week=當前顯示班表 / month=該月所有已發布班表
      monthRows: null,      // 月統計的 assignments（lazy load）
      loading: true,
      busy: false,
      error: null,
    };
  }

  async connectedCallback() {
    this.render();
    await this.load();
  }

  async load() {
    this.state.loading = true;
    this.render();
    const supabase = getSupabase();
    const { weekStart } = this.state;
    try {
      const [sched, tpl, emp] = await Promise.all([
        supabase.from('schedules')
          .select('id, period_start, period_end, status, generated_by, demand_snapshot, published_at')
          .eq('period_start', weekStart)
          .neq('status', 'archived')
          .order('status', { ascending: false })   // published > draft（字母序剛好 p > d）
          .order('created_at', { ascending: false })
          .limit(1).maybeSingle(),
        // 全部模板（含停用 — 月統計要查歷史班次時長），grid 只畫 active
        supabase.from('shift_templates')
          .select('id, name, start_time, end_time, weekdays, is_active')
          .order('start_time', { ascending: true }),
        supabase.from('employees').select('id, name, role, hourly_rate'),
      ]);
      this.state.schedule = sched.data || null;
      this.state.templates = tpl.data || [];
      this.state.employees = emp.data || [];
      if (this.state.schedule) {
        const { data: rows } = await supabase.from('shift_assignments')
          .select('date, shift_template_id, employee_id, conflict_notes')
          .eq('schedule_id', this.state.schedule.id);
        this.state.assignments = rows || [];
      } else {
        this.state.assignments = [];
      }
      this.state.error = null;
    } catch (err) {
      this.state.error = err.message_zh || err.message || '載入失敗';
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  async shiftWeek(n) {
    this.state.weekStart = addDays(this.state.weekStart, n * 7);
    this.state.violations = [];
    this.state.monthRows = null;  // 跨週可能跨月，月統計重抓
    await this.load();
  }

  // ─── 人力統計 ───
  async setStatsScope(scope) {
    this.state.statsScope = scope;
    if (scope === 'month' && !this.state.monthRows) await this.loadMonthStats();
    this.render();
  }

  /** 該月所有「已發布」班表的 assignments（草稿不計薪） */
  async loadMonthStats() {
    const monthStart = `${this.state.weekStart.slice(0, 7)}-01`;
    const nextMonthStart = `${addDays(monthStart, 35).slice(0, 7)}-01`;
    const { data, error } = await getSupabase().from('shift_assignments')
      .select('date, shift_template_id, employee_id, schedules!inner(status)')
      .gte('date', monthStart).lt('date', nextMonthStart)
      .eq('schedules.status', 'published');
    if (error) {
      alert('月統計載入失敗：' + error.message);
      this.state.monthRows = [];
      return;
    }
    this.state.monthRows = data || [];
  }

  /** rows → 每員工 {班數/天數/時數/薪資估算}，按角色排序 */
  computeStats(rows) {
    const { templates, employees } = this.state;
    const hoursOf = new Map(templates.map((t) => [t.id, shiftHrs(t.start_time, t.end_time)]));
    const agg = new Map();
    for (const r of rows) {
      if (!agg.has(r.employee_id)) agg.set(r.employee_id, { shifts: 0, days: new Set(), hours: 0 });
      const a = agg.get(r.employee_id);
      a.shifts += 1;
      a.days.add(r.date);
      a.hours += hoursOf.get(r.shift_template_id) || 0;
    }
    const order = { chef: 0, fulltime: 1, parttime: 2 };
    return [...agg.entries()]
      .map(([id, a]) => {
        const e = employees.find((x) => x.id === id) || {};
        const rate = e.hourly_rate != null ? Number(e.hourly_rate) : null;
        return {
          name: e.name || '？', role: e.role || '?', rate,
          shifts: a.shifts, days: a.days.size,
          hours: Math.round(a.hours * 10) / 10,
          pay: rate != null ? Math.round(a.hours * rate) : null,
        };
      })
      .sort((x, y) => (order[x.role] ?? 9) - (order[y.role] ?? 9) || x.name.localeCompare(y.name));
  }

  async generate() {
    if (this.state.schedule?.status === 'published'
      && !confirm('本週已有發布班表。重新生成會建立新草稿（不影響已發布版），繼續？')) return;
    this.state.busy = true;
    this.render();
    try {
      const res = await generateSchedule({ periodStart: this.state.weekStart });
      this.state.violations = res.violations || [];
      await this.load();
      // load() 可能抓到 published 優先；剛生成的是 draft → 若有 published 提示
    } catch (err) {
      alert('生成失敗：' + (err.message_zh || err.message || '未知錯誤'));
    } finally {
      this.state.busy = false;
      this.render();
    }
  }

  async publish() {
    const { schedule } = this.state;
    if (!schedule || schedule.status !== 'draft') return;
    this.state.busy = true;
    this.render();
    const { error } = await getSupabase().from('schedules')
      .update({ status: 'published', published_at: new Date().toISOString() })
      .eq('id', schedule.id);
    this.state.busy = false;
    if (error) {
      alert(error.code === '23505'
        ? '本週已有另一份已發布班表，請先將它改回草稿'
        : '發布失敗：' + error.message);
    }
    await this.load();
  }

  async unpublish() {
    const { schedule } = this.state;
    if (!schedule || schedule.status !== 'published') return;
    if (!confirm('將已發布班表改回草稿？')) return;
    const { error } = await getSupabase().from('schedules')
      .update({ status: 'draft', published_at: null })
      .eq('id', schedule.id);
    if (error) alert('操作失敗：' + error.message);
    await this.load();
  }

  render() {
    const { weekStart, schedule, assignments, templates, employees,
            violations, statsScope, monthRows, loading, busy, error } = this.state;
    const weekEnd = addDays(weekStart, 6);
    const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const empName = (id) => employees.find((e) => e.id === id)?.name || '？';
    const cell = (tplId, date) => assignments
      .filter((a) => a.shift_template_id === tplId && a.date === date);
    const demandItems = schedule?.demand_snapshot?.items || [];
    const gridTemplates = templates.filter((t) => t.is_active !== false);
    const statsRows = statsScope === 'month'
      ? this.computeStats(monthRows || [])
      : (schedule ? this.computeStats(assignments) : []);
    const totalPay = statsRows.reduce((s, r) => s + (r.pay || 0), 0);

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>
      <main>
        <header class="head">
          <h1>一鍵生成班表</h1>
          <div class="week-nav">
            <button data-act="prev" ${busy ? 'disabled' : ''}>‹</button>
            <span class="week-label">${fmtMD(weekStart)} – ${fmtMD(weekEnd)}</span>
            <button data-act="next" ${busy ? 'disabled' : ''}>›</button>
          </div>
          <div class="actions">
            ${schedule ? `<span class="badge badge--${schedule.status}">${schedule.status === 'published' ? '✓ 已發布' : '草稿'}</span>` : ''}
            <button class="primary" data-act="generate" ${busy ? 'disabled' : ''}>
              ${busy ? '處理中…' : schedule ? '🔄 重新生成' : '✨ 生成班表'}
            </button>
            ${schedule?.status === 'draft' ? `<button class="publish" data-act="publish" ${busy ? 'disabled' : ''}>📣 發布</button>` : ''}
            ${schedule?.status === 'published' ? `<button data-act="unpublish" ${busy ? 'disabled' : ''}>改回草稿</button>` : ''}
          </div>
        </header>

        ${error ? `<div class="banner err">✗ ${escapeHtml(error)}</div>` : ''}
        ${loading ? '<div class="banner loading">載入中…</div>' : ''}

        ${!loading && violations.length ? `
          <div class="banner warn">
            <strong>⚠️ ${violations.length} 個排班缺口</strong>
            <ul>${violations.slice(0, 8).map((v) => `
              <li>${escapeHtml(v.date)} ${escapeHtml(v.shift)}：
                ${v.type === 'understaffed_role'
                  ? `缺「${escapeHtml(v.role === 'chef' ? '主廚' : v.role)}」（需 ${v.need}）`
                  : `人手不足（需 ${v.need}，排到 ${v.got}）`}</li>`).join('')}
            </ul>
            <small>調整員工固休/期望假或降低班別最低人力後重新生成。</small>
          </div>` : ''}

        ${!loading && schedule?.ai_explanation ? `
          <div class="xiaoban">
            <span class="xiaoban__avatar">💬</span>
            <div>
              <strong>小掌櫃</strong>
              <p>${escapeHtml(schedule.ai_explanation)}</p>
            </div>
          </div>` : ''}

        ${!loading && !schedule ? `
          <div class="empty-state">
            <p>本週還沒有班表。</p>
            <p class="hint">需要先在 <a href="#/settings/scheduling">設定 → 排班</a> 建立員工與班別模板，再按「生成班表」。</p>
          </div>` : ''}

        ${!loading && schedule ? `
          <div class="grid-wrap">
            <table class="grid">
              <thead>
                <tr>
                  <th class="shift-col">班別</th>
                  ${dates.map((d) => `<th>${fmtMD(d)}<br/><small>週${WD_LABELS[new Date(`${d}T00:00:00Z`).getUTCDay()]}</small></th>`).join('')}
                </tr>
              </thead>
              <tbody>
                ${gridTemplates.map((t) => `
                  <tr>
                    <td class="shift-col">
                      <strong>${escapeHtml(t.name)}</strong><br/>
                      <small>${escapeHtml(String(t.start_time).slice(0, 5))}–${escapeHtml(String(t.end_time).slice(0, 5))}</small>
                    </td>
                    ${dates.map((d) => {
                      const wd = new Date(`${d}T00:00:00Z`).getUTCDay();
                      if (!(t.weekdays || []).includes(wd)) return '<td class="off">—</td>';
                      const list = cell(t.id, d);
                      const dm = demandItems.find((x) => x.date === d && x.shift === t.name);
                      return `<td>
                        ${list.map((a) => `
                          <span class="emp ${a.conflict_notes ? 'has-note' : ''}"
                                ${a.conflict_notes ? `title="${escapeHtml(a.conflict_notes)}"` : ''}>
                            ${escapeHtml(empName(a.employee_id))}${a.conflict_notes ? ' ⚠' : ''}
                          </span>`).join('')}
                        ${dm && list.length < dm.suggested ? `<span class="short">缺 ${dm.suggested - list.length}</span>` : ''}
                      </td>`;
                    }).join('')}
                  </tr>`).join('')}
              </tbody>
            </table>
          </div>

          <div class="stats">
            <div class="stats__head">
              <h2>📋 人力統計</h2>
              <div class="scope-toggle">
                <button data-scope="week" data-on="${statsScope === 'week'}">本週${schedule?.status === 'draft' ? '（草稿）' : ''}</button>
                <button data-scope="month" data-on="${statsScope === 'month'}">本月（已發布）</button>
              </div>
            </div>
            ${statsRows.length ? `
              <table class="stats-table">
                <thead><tr><th>員工</th><th>角色</th><th>班數</th><th>出勤天數</th><th>總時數</th><th>時薪</th><th>預估薪資</th></tr></thead>
                <tbody>
                  ${statsRows.map((r) => `
                    <tr>
                      <td><strong>${escapeHtml(r.name)}</strong></td>
                      <td>${ROLE_LABELS[r.role] || escapeHtml(r.role)}</td>
                      <td>${r.shifts}</td>
                      <td>${r.days} 天</td>
                      <td>${r.hours} h</td>
                      <td>${r.rate != null ? `NT$ ${r.rate}` : '—'}</td>
                      <td>${r.pay != null ? `<strong>NT$ ${r.pay.toLocaleString('zh-TW')}</strong>` : '<span class="dim">月薪另計</span>'}</td>
                    </tr>`).join('')}
                </tbody>
                ${totalPay ? `<tfoot><tr><td colspan="6">時薪制預估小計</td><td><strong>NT$ ${totalPay.toLocaleString('zh-TW')}</strong></td></tr></tfoot>` : ''}
              </table>
              <p class="stats-note">薪資為估算（時薪 × 排班時數），未含加班費/勞健保；實際以出勤為準。時薪在「設定 → 排班」員工列表填寫，月薪制員工留空即可。</p>
            ` : `<p class="stats-note">${statsScope === 'month' ? '本月還沒有已發布的班表。' : '本週沒有排班資料。'}</p>`}
          </div>

          ${demandItems.length ? `
            <details class="demand">
              <summary>📊 需求預測依據（過去 4 週銷售 + 本週訂位）</summary>
              <table>
                <thead><tr><th>日期</th><th>班別</th><th>基準</th><th>平均單量/班</th><th>訂位人次</th><th>建議人力</th></tr></thead>
                <tbody>
                  ${demandItems.filter((x) => x.suggested > x.base_min).map((x) => `
                    <tr><td>${escapeHtml(x.date)}</td><td>${escapeHtml(x.shift)}</td>
                        <td>${x.base_min}</td><td>${x.avg_orders}</td>
                        <td>${x.reserved_seats}</td><td><strong>${x.suggested}</strong></td></tr>`).join('')
                    || '<tr><td colspan="6">本週各班需求皆為基準人力（無加班需求訊號）</td></tr>'}
                </tbody>
              </table>
            </details>` : ''}
        ` : ''}
      </main>
    `;
    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    const on = (act, fn) => root.querySelector(`[data-act="${act}"]`)
      ?.addEventListener('click', fn);
    on('prev', () => this.shiftWeek(-1));
    on('next', () => this.shiftWeek(1));
    on('generate', () => this.generate());
    on('publish', () => this.publish());
    on('unpublish', () => this.unpublish());
    root.querySelectorAll('[data-scope]').forEach((btn) =>
      btn.addEventListener('click', () => this.setStatsScope(btn.dataset.scope)));
  }

  styles() {
    return `
      :host { display: block; color: var(--fg-base); background: var(--bg-base); min-height: calc(100vh - 60px); font-family: var(--font-sans); }
      main { padding: var(--s-6) var(--s-8); display: grid; gap: var(--s-5); align-content: start; }
      .head { display: flex; align-items: center; gap: var(--s-5); flex-wrap: wrap; }
      .head h1 { font-size: 1.4rem; font-weight: 600; margin: 0; }
      .week-nav { display: flex; align-items: center; gap: var(--s-2); }
      .week-label { font-weight: 600; min-width: 110px; text-align: center; font-variant-numeric: tabular-nums; }
      .actions { display: flex; align-items: center; gap: var(--s-3); margin-left: auto; }
      button { font-family: inherit; cursor: pointer; border-radius: var(--r-md); border: 1px solid var(--border); background: var(--bg-surface); color: var(--fg-base); min-height: 40px; padding: var(--s-2) var(--s-4); }
      button:disabled { opacity: .5; cursor: default; }
      button.primary { background: var(--brand-primary); color: var(--fg-on-brand); border: none; font-weight: 600; }
      button.publish { background: var(--success, #16a34a); color: #fff; border: none; font-weight: 600; }
      .badge { padding: 2px 10px; border-radius: var(--r-full); font-size: .8rem; }
      .badge--published { background: rgba(22,163,74,.15); color: var(--success, #16a34a); }
      .badge--draft { background: rgba(232,88,12,.12); color: var(--brand-primary); }
      .banner { padding: var(--s-3) var(--s-4); border-radius: var(--r-md); }
      .banner.err { background: rgba(239,68,68,.1); color: var(--danger); }
      .banner.loading { background: rgba(59,130,246,.1); color: var(--fg-muted); }
      .banner.warn { background: rgba(196,125,0,.1); color: var(--fg-base); display: grid; gap: var(--s-2); }
      .banner.warn ul { margin: 0; padding-left: var(--s-5); }
      .banner.warn small { color: var(--fg-muted); }
      .xiaoban { display: flex; gap: var(--s-3); align-items: flex-start; padding: var(--s-4); background: var(--bg-elevated); border: 1px solid var(--border); border-left: 3px solid var(--brand-primary); border-radius: var(--r-lg); }
      .xiaoban__avatar { font-size: 1.4rem; }
      .xiaoban p { margin: var(--s-1) 0 0; color: var(--fg-base); font-size: .95rem; line-height: 1.6; }
      .empty-state { text-align: center; padding: var(--s-10); color: var(--fg-muted); }
      .empty-state a { color: var(--brand-primary); }
      .grid-wrap { overflow-x: auto; }
      table.grid { width: 100%; border-collapse: collapse; font-size: .9rem; min-width: 760px; }
      table.grid th, table.grid td { border: 1px solid var(--border); padding: var(--s-2) var(--s-3); text-align: center; vertical-align: top; }
      table.grid thead th { background: var(--bg-elevated); font-size: .8rem; }
      td.shift-col, th.shift-col { text-align: left; background: var(--bg-elevated); white-space: nowrap; }
      td.off { color: var(--fg-dim); background: var(--bg-surface); }
      .emp { display: block; padding: 2px 6px; margin: 2px 0; background: var(--bg-elevated); border-radius: var(--r-sm); font-size: .85rem; }
      .emp.has-note { background: rgba(196,125,0,.15); cursor: help; }
      .short { display: block; color: var(--danger); font-size: .75rem; margin-top: 2px; }
      .stats { display: grid; gap: var(--s-3); padding: var(--s-4); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); }
      .stats__head { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--s-3); }
      .stats__head h2 { font-size: 1.05rem; font-weight: 600; margin: 0; }
      .scope-toggle { display: flex; gap: var(--s-1); }
      .scope-toggle button { min-height: 34px; padding: var(--s-1) var(--s-3); font-size: .85rem; }
      .scope-toggle button[data-on="true"] { background: var(--brand-primary); color: var(--fg-on-brand); border-color: var(--brand-primary); }
      .stats-table { width: 100%; border-collapse: collapse; font-size: .9rem; }
      .stats-table th, .stats-table td { padding: var(--s-2) var(--s-3); text-align: left; border-bottom: 1px solid var(--border); }
      .stats-table th { font-size: .75rem; text-transform: uppercase; letter-spacing: 1px; color: var(--fg-dim); font-weight: 500; }
      .stats-table tfoot td { border-bottom: none; border-top: 2px solid var(--border-strong); }
      .stats-note { margin: 0; font-size: .78rem; color: var(--fg-dim); }
      .dim { color: var(--fg-dim); font-size: .85rem; }
      .demand summary { cursor: pointer; color: var(--fg-muted); font-size: .9rem; }
      .demand table { width: 100%; border-collapse: collapse; font-size: .85rem; margin-top: var(--s-3); }
      .demand th, .demand td { padding: var(--s-1) var(--s-3); border-bottom: 1px solid var(--border); text-align: left; }
    `;
  }
}

customElements.define('pos-scheduling', PosScheduling);
